/**
 * Getting a stale tab onto the current build.
 *
 * A plain `location.reload()` is not enough here and never was. iTow installs a
 * Workbox service worker that precaches the app shell, so the reloaded document
 * can be answered by the same worker from the same cache — which is exactly the
 * loop people were stuck in: reload, still stale, reload again. The manual
 * incantation everyone in this project has been pasting into a console
 * (unregister the workers, drop every cache, reload) is what actually works, so
 * that is what this does.
 *
 * Kept out of the React tree deliberately — it must be callable from a plain
 * event handler, and it tears down the very machinery a component would need.
 */

/** Milliseconds to wait for the teardown before reloading anyway. */
const TEARDOWN_TIMEOUT_MS = 3_000;

/**
 * Unregisters every service worker and deletes every Cache Storage entry, then
 * reloads.
 *
 * The teardown is best-effort and time-boxed. A browser that refuses to
 * enumerate its caches (private mode, a policy-locked profile) must not be able
 * to strand someone on a build they have been told to leave — so the reload
 * happens either way, and the worst case is the ordinary reload they would have
 * got without this.
 */
export async function reloadToLatestBuild(): Promise<void> {
  const teardown = (async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  })().catch(() => {
    // Nothing actionable: we are about to reload regardless, and reporting a
    // cache-teardown failure to someone who clicked "Reload" helps no one.
  });

  await Promise.race([
    teardown,
    new Promise((resolve) => setTimeout(resolve, TEARDOWN_TIMEOUT_MS)),
  ]);

  // `location.reload()` may still be served by the HTTP cache for the document
  // itself. A cache-busting query on the same path is answered by the hosting
  // rewrite exactly like the original URL, and cannot be a cache hit.
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString(36));
  window.location.replace(url.toString());
}
